import {
  CorrectionMetaData,
  CorrectionRawResponse,
  Corrections,
  CorrectionsData,
  LastModified,
  MaterialsRawResponse,
  ScaleTicket,
  Transaction,
  TicketsRawResponse,
  ScaleTicketCorrectionsForm,
} from "../types";

export const getLastModified = (
  obj: any,
  key: string | number,
  prefix: string = ""
): CorrectionMetaData<LastModified> =>
  obj?.updatedBy && {
    fieldName: prefix ? `${prefix}.${key}` : `${key}`,
    updatedBy: obj.updatedBy,
    updatedByCompanyId: obj.updatedByCompanyId,
    modifiedDate: obj.modifiedDate,
  };

const getCorrectedMaterials = (
  initial: MaterialsRawResponse[],
  corrected: CorrectionMetaData<MaterialsRawResponse>[]
) => {
  const initialByCode = initial.reduce((res, item) => {
    res[item.materialCode] = item;
    return res;
  }, {} as Record<string, MaterialsRawResponse>);

  const correctedByCode = corrected.reduce((res, item) => {
    res[item.materialCode] = item;
    return res;
  }, {} as Record<string, CorrectionMetaData<MaterialsRawResponse>>);

  return Object.values({ ...initialByCode, ...correctedByCode })
    .filter((item) => item.units.unitQuantity !== 0)
    .map(({ materialCode, units }) => ({
      materialCode,
      units,
    }));
};

export const getScaleTickets = (
  initial: ScaleTicket[],
  correctedResponse: CorrectionMetaData<TicketsRawResponse>[]
): ScaleTicketCorrectionsForm[] => {
  const correctedTicketsBySortOrder = correctedResponse.reduce(
    (acc, ticket) => {
      acc[ticket.sortOrder - 1] = ticket;
      return acc;
    },
    {} as Record<number, CorrectionMetaData<TicketsRawResponse>>
  );

  const res = new Array(2).fill(null).reduce((acc, _, index) => {
    const original = initial[index];
    const corrected = original?.ticketId
      ? correctedResponse.find((ticket) => ticket.ticketId === original.ticketId) ||
        correctedResponse.find((ticket) => !ticket.ticketId && ticket.sortOrder === (original.sortOrder ?? index + 1))
      : correctedTicketsBySortOrder[index];
    if (corrected) {
      acc[index] = {
        ticketId: corrected.ticketId ?? original?.ticketId,
        initialTicketNumber: corrected.initialTicketNumber,
        correctedTicketNumber: corrected.ticketNumber,
      };
    } else if (initial[index]) {
      acc[index] = {
        ticketId: initial[index].ticketId,
        initialTicketNumber: initial[index].ticketNumber,
        correctedTicketNumber: initial[index].ticketNumber,
      };
    }
    return acc;
  }, [] as ScaleTicketCorrectionsForm[]);

  return res.filter(Boolean);
};

export const correctionResponseMapper = (
  correctionResponse: CorrectionRawResponse,
  transaction: Transaction
): Corrections => {
  const {
    initialMaterials,
    correctedMaterials,
    initialGrossWeight,
    correctedGrossWeight,
    initialTareWeight,
    correctedTareWeight,
    correctedTickets,
    correctedDocuments,
    correctedComments,
    correctedMaterialsAttributesData,
  } = correctionResponse;

  const scaleTickets = getScaleTickets(
    transaction.scaleTickets,
    correctedTickets
  );

  const initialData: CorrectionsData = {
    materials: initialMaterials!.map(
      ({ materialCode, units }) => ({
        materialCode,
        units,
      })
    ),
    materialAttributes: [],
    scaleTickets,
    grossWeight: initialGrossWeight?.weight,
    tareWeight: initialTareWeight?.weight,
    commentsData: [],
    documentsData: [],
  };

  const correctedData: CorrectionsData = {
    materials: getCorrectedMaterials(initialMaterials, correctedMaterials),
    materialAttributes:
      correctedMaterialsAttributesData?.correctedMaterialAttributes ||
      transaction.materialAttributes ||
      [],
    scaleTickets,
    grossWeight: correctedGrossWeight
      ? correctedGrossWeight?.weight
      : initialGrossWeight?.weight,
    tareWeight: correctedTareWeight
      ? correctedTareWeight?.weight
      : initialTareWeight?.weight,
    commentsData: correctedComments || [],
    documentsData: correctedDocuments || [],
  };

  const lastModified: CorrectionMetaData<LastModified>[] = [
    ...correctedMaterials.map((item) =>
      getLastModified(item, item.materialCode, "materials")
    ),
    getLastModified(correctedMaterialsAttributesData, "materialAttributes"),
    ...correctedTickets.map((item) => {
      const index = item.ticketId
        ? scaleTickets.findIndex((ticket) => ticket.ticketId === item.ticketId)
        : item.sortOrder - 1;
      return index >= 0 ? getLastModified(item, index, "scaleTickets") : undefined;
    }),
    getLastModified(correctedGrossWeight, "grossWeight"),
    getLastModified(correctedTareWeight, "tareWeight"),
  ].filter(Boolean);

  return {
    isSubmitted: correctionResponse.isSubmitted,
    lastModifiedBy: correctionResponse.lastModifiedBy,
    requestTitle: correctionResponse.requestTitle,
    lastModifiedByCompanyId: correctionResponse.lastModifiedByCompanyId,
    initialData,
    correctedData,
    lastModified,
  };
};

export default correctionResponseMapper;
